/**
 * Combined page interaction layer (mirrors DetailPageController): owns
 * which dialog is open + the downloads hook, hands typed handlers to the
 * static composition. Decision dialogs post the ENTRY application's id —
 * decideHousehold expands to the whole household server-side, so a
 * combined-page decision and an individual-page decision are the same
 * operation (COMBINED-V2-DATA-MAP §3).
 */
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import {
	ReachOutDialog,
	type ReachOutMember,
} from '#app/components/application-detail-v2/vendor/ReachOutDialog'
import { RenewCreditsDialog } from '#app/components/billing/RenewCreditsDialog'
import {
	countEligibleCoResidents,
	countEligibleGuarantors,
	deriveFamilyReportAction,
} from '#app/utils/application-detail-v2/combined-display'
import type { CombinedReportData } from '#app/utils/application-detail-v2/combined-types'
import { preserveReturnTo } from '#app/utils/application-detail-v2/return-to'
import type {
	GroupMember,
	HouseholdChild,
} from '#app/utils/application-detail-v2/types'
import { isDecisionLocked } from '#app/utils/landlord-application-actions'
import { HeaderProfileMenu } from '../HeaderProfileMenu'
import { AddApplicantDialog } from '../dialogs/AddApplicantDialog'
import { DecisionDialog, type DecisionIntent } from '../dialogs/DecisionDialog'
import { DeleteApplicationDialog } from '../dialogs/DeleteApplicationDialog'
import { ProceedToFullScreenDialog } from '../dialogs/ProceedToFullScreenDialog'
import { ReinstateDialog } from '../dialogs/ReinstateDialog'
import { RequestBankDialog } from '../dialogs/RequestBankDialog'
import { RequestDocumentsDialog } from '../dialogs/RequestDocumentsDialog'
import { RequestGuarantorDialog } from '../dialogs/RequestGuarantorDialog'
import { SendUpdateLinkDialog } from '../dialogs/SendUpdateLinkDialog'
import { TimelinePanel } from '../dialogs/TimelinePanel'
import { useDownloads } from '../downloads/useDownloads'
import { CombinedReportPage } from './CombinedReportPage'
import { FamilyRegenerateDialog } from './FamilyRegenerateDialog'
import { RemoveChildDialog } from './RemoveChildDialog'
import { RemoveMemberDialog } from './RemoveMemberDialog'

type DialogKind =
	| DecisionIntent
	| 'addApplicant'
	| 'guarantor'
	| 'bank'
	| 'requestDocuments'
	| 'reachOut'
	| 'delete'
	| 'fullScreen'
	| 'sendLink'
	| 'familyRegenerate'
	| 'timeline'
	| 'reinstate'

// Abandon retired as an offered action (owner 2026-07-17) — legacy
// abandoned rows still display; the migration script moves them off.
const DECISION_INTENTS: DecisionIntent[] = ['approve', 'decline', 'withdraw']

export function CombinedController({ data }: { data: CombinedReportData }) {
	const navigate = useNavigate()
	// Deep-link: the dashboard Action Center's "View Timeline" lands here with
	// `?view=timeline` (household cards link to /combined) — open the shared
	// timeline dialog on arrival, stripping the param on close (render-time
	// init, no useEffect; same pattern as DetailPageController).
	const [searchParams, setSearchParams] = useSearchParams()
	const [dialog, setDialog] = useState<DialogKind | null>(
		searchParams.get('view') === 'timeline' ? 'timeline' : null,
	)
	/** Independent of `dialog` so guarantor confirm can open top-up without
	 * losing the pending send. */
	const [topupOpen, setTopupOpen] = useState(false)
	const clearViewParam = () => {
		if (!searchParams.has('view')) return
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev)
				next.delete('view')
				return next
			},
			{ replace: true },
		)
	}
	const [memberToRemove, setMemberToRemove] = useState<GroupMember | null>(null)
	const [childToRemove, setChildToRemove] = useState<HouseholdChild | null>(
		null,
	)
	const downloadsApi = useDownloads({
		application: data.application,
		downloads: data.downloads,
		credit: null, // combined page exposes no per-member credit artifacts
		veriffDocuments: [],
		householdMembers: data.members, // drives "Download All Files"
	})

	const openChange = (kind: DialogKind) => (open: boolean) =>
		setDialog(open ? kind : null)

	// Family (re)generate offering — same pure gate the page uses for the kebab
	// item + failed-card CTA. `decisionLocked` mirrors the page's household
	// terminal check so a decided group can't re-run a report (rule 3).
	// Regenerate locks on ANY recorded decision (rule 3) — an accepted
	// household keeps Withdraw (rule 1 carve-out), so the actions map no
	// longer implies "locked".
	const decisionLocked = isDecisionLocked(data.groupStatus)
	const familyReportAction = deriveFamilyReportAction({
		canRegenerate: data.canRegenerate,
		decisionLocked,
		familyStatus: data.familyScore?.processingStatus ?? null,
		eligibleCoResidentCount: countEligibleCoResidents(data.members),
		eligibleGuarantorCount: countEligibleGuarantors(data.members),
		isStudentHousing: data.applicationCategory === 'student_housing',
		preScreenApplicantCount: data.preScreenApplicants.length,
		allApplicantsGuarantorLinked: data.studentGuarantorLinksReady,
	})

	const memberInitials = (name: string) => {
		const parts = name.trim().split(/\s+/).filter(Boolean)
		if (parts.length === 0) return '?'
		if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
		return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
	}
	const reachOutMembers = data.members.map((member) => ({
		key: member.applicationId,
		fullName: member.displayName,
		shortName: member.displayName,
		initials: memberInitials(member.displayName),
		roleLabel: member.relationshipLabel,
		phone: member.phone ?? null,
	})) satisfies ReachOutMember[]

	return (
		<>
			<CombinedReportPage
				data={data}
				downloadsApi={downloadsApi}
				// The route hides the shared SiteHeader, so the account menu is
				// rendered HERE (inside the data router) and slotted into the
				// page's own top bar — same as DetailPageController.
				profileMenu={<HeaderProfileMenu />}
				handlers={{
					onApprove: () => setDialog('approve'),
					onDecline: () => setDialog('decline'),
					onWithdraw: () => setDialog('withdraw'),
					onReinstate: () => setDialog('reinstate'),
					onSendUpdateLink: () => setDialog('sendLink'),
					onAddApplicant: () => setDialog('addApplicant'),
					onRequestGuarantor: () => setDialog('guarantor'),
					// Household-wide requests: both dialogs pick their targets from
					// the member list, so they work identically here and on the
					// individual page (client 2026-08-27).
					onRequestBankAccount: () => setDialog('bank'),
					onRequestDocuments: () => setDialog('requestDocuments'),
					onReachOut: () => setDialog('reachOut'),
					onDeleteApplication: () => setDialog('delete'),
					onProceedToFullScreen: () => setDialog('fullScreen'),
					onOpenTopup: data.billing?.canPurchaseCredits
						? () => setTopupOpen(true)
						: undefined,
					onRemoveMember: (member) => setMemberToRemove(member),
					onRemoveChild: (child) => setChildToRemove(child),
					onRegenerateReport: () => setDialog('familyRegenerate'),
					onOpenTimeline: () => setDialog('timeline'),
				}}
			/>

			{DECISION_INTENTS.map((intent) => (
				<DecisionDialog
					key={intent}
					intent={intent}
					open={dialog === intent}
					onOpenChange={openChange(intent)}
					// v3: the dialog is member-scoped from ANY entry — the engine
					// loads the whole household by groupId, so the guarantor-entry
					// re-anchor (2026-07-15) is retired.
					application={data.application}
					groupMembers={data.members}
					// Decisions v4 combined console: every undecided member seeds the
					// clicked intent; invite rows are identity-only (pills hidden,
					// default Cancel) under the bottom banner — Keep stays reachable
					// from the individual page's console (default Keep there).
					consoleVariant="combined"
					pendingInvitations={data.pendingInvitees.map((invitee) => ({
						id: invitee.id,
						invitedEmail: invitee.invitedEmail,
						invitedName: invitee.invitedName,
						invitationType:
							invitee.invitationType === 'guarantor'
								? ('guarantor' as const)
								: ('co_applicant' as const),
						relationship: invitee.relationship,
					}))}
					property={data.property}
					memberTimelines={data.memberTimelines}
				/>
			))}

			<ProceedToFullScreenDialog
				open={dialog === 'fullScreen'}
				onOpenChange={openChange('fullScreen')}
				application={data.application}
				// Every applicant still on pre-screening — guarantors are exempt
				// (they never count toward the household report).
				targets={data.members
					.filter(
						(member) =>
							member.applicationType !== 'guarantor' &&
							member.screeningKind === 'pre_screening',
					)
					.map((member) => ({
						id: member.applicationId,
						name: member.displayName,
					}))}
			/>
			<RequestBankDialog
				open={dialog === 'bank'}
				onOpenChange={openChange('bank')}
				application={data.application}
				groupMembers={data.members}
			/>
			<RequestDocumentsDialog
				open={dialog === 'requestDocuments'}
				onOpenChange={openChange('requestDocuments')}
				application={data.application}
				groupMembers={data.members}
			/>
			<ReachOutDialog
				open={dialog === 'reachOut'}
				onOpenChange={openChange('reachOut')}
				members={reachOutMembers}
			/>
			<DeleteApplicationDialog
				open={dialog === 'delete'}
				onOpenChange={openChange('delete')}
				application={data.application}
			/>
			<AddApplicantDialog
				open={dialog === 'addApplicant'}
				onOpenChange={openChange('addApplicant')}
				application={data.application}
			/>
			<RequestGuarantorDialog
				open={dialog === 'guarantor'}
				onOpenChange={openChange('guarantor')}
				application={data.application}
				members={data.members}
				property={data.property}
				guarantorUpgradeEnabled={data.applicationCategory !== 'student_housing'}
				walletBalance={data.billing?.balance ?? null}
				upgradeCreditsPerApplicant={data.fullScreenUpgradeCredits}
				onTopUp={data.billing ? () => setTopupOpen(true) : undefined}
			/>
			<SendUpdateLinkDialog
				open={dialog === 'sendLink'}
				onOpenChange={openChange('sendLink')}
				application={data.application}
				groupMembers={data.members}
			/>
			<RemoveMemberDialog
				open={memberToRemove !== null}
				onOpenChange={(open) => {
					if (!open) setMemberToRemove(null)
				}}
				member={memberToRemove}
				onRemoved={(member) => {
					// Removal DETACHES the member from the group — if that member is
					// the ENTRY application, this combined URL now anchors an
					// ungrouped application (a misleading household-of-one view), so
					// leave first: to the primary's combined page (it survives), or
					// the property's applications list when the removed entry WAS the
					// primary (the server promoted a co-applicant we can't identify
					// client-side).
					if (member.applicationId !== data.application.id) return
					const primary = data.members.find(
						(m) =>
							m.applicationType === 'primary' &&
							m.applicationId !== member.applicationId,
					)
					const base = `/properties/${data.application.propertyId}/applications`
					void navigate(
						primary
							? preserveReturnTo(
									`${base}/${primary.applicationId}/v2/combined`,
									searchParams,
								)
							: base,
					)
				}}
			/>
			<RemoveChildDialog
				open={childToRemove !== null}
				onOpenChange={(open) => {
					if (!open) setChildToRemove(null)
				}}
				child={childToRemove}
				entryApplicationId={data.application.id}
			/>
			<FamilyRegenerateDialog
				open={dialog === 'familyRegenerate'}
				onOpenChange={openChange('familyRegenerate')}
				groupId={data.application.groupId}
				members={data.members}
				mode={familyReportAction ?? 'regenerate'}
				isStudentHousing={data.applicationCategory === 'student_housing'}
			/>
			{data.billing?.canPurchaseCredits ? (
				<RenewCreditsDialog
					open={topupOpen}
					onOpenChange={setTopupOpen}
					currentBalance={data.billing.balance}
					ratio={data.billing.ratio}
					minDollars={data.billing.minTopupDollars}
				/>
			) : null}
			<TimelinePanel
				open={dialog === 'timeline'}
				onOpenChange={(open) => {
					setDialog(open ? 'timeline' : null)
					if (!open) clearViewParam()
				}}
				header={{
					bedrooms: data.property.bedrooms,
					moveInDate: data.primaryPropertyStep?.moveInDate,
					applicationDate: data.application.applicationDate,
				}}
				members={data.memberTimelines}
				// Entry member pre-selected; every member's View Application jumps
				// to their own individual page (we're on the combined view).
				defaultApplicationId={data.application.id}
				memberHref={(member) =>
					preserveReturnTo(
						`/properties/${member.propertyId ?? data.application.propertyId}/applications/${member.applicationId}/v2`,
						searchParams,
					)
				}
			/>
			<ReinstateDialog
				open={dialog === 'reinstate'}
				onOpenChange={openChange('reinstate')}
				application={data.application}
			/>
		</>
	)
}
